import React, { useState, useEffect } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useAuth } from '@/contexts/AuthContext';

const CustomTooltip = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
    isLastStep,
}) => {
    const handleSkip = (e) => {
        try {
            localStorage.setItem('oblique_tour_completed', 'true');
            localStorage.setItem('oblique_walkthrough_dismissed', 'true');
            localStorage.removeItem('oblique_force_tour');
            if (user?.uid) {
                localStorage.setItem(`oblique_tour_completed_${user.uid}`, 'true');
            }
        } catch (_) {}
        if (skipProps && skipProps.onClick) skipProps.onClick(e);
    };

    const handlePrimary = (e) => {
        if (isLastStep) {
            try {
                localStorage.setItem('oblique_tour_completed', 'true');
                localStorage.setItem('oblique_walkthrough_dismissed', 'true');
                localStorage.removeItem('oblique_force_tour');
                if (user?.uid) {
                    localStorage.setItem(`oblique_tour_completed_${user.uid}`, 'true');
                }
            } catch (_) {}
        }
        if (primaryProps && primaryProps.onClick) primaryProps.onClick(e);
    };

    return (
        <div {...tooltipProps} style={{ 
            backgroundColor: '#fff', 
            borderRadius: '12px', 
            width: '320px',
            padding: '20px', 
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
        }}>
            {step.content}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px' }}>
                <button {...skipProps} onClick={handleSkip} style={{ 
                    background: 'transparent', border: 'none', color: '#6B7280', 
                    fontSize: '13px', fontWeight: '500', cursor: 'pointer', padding: '4px 8px' 
                }}>
                    Skip Tour
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {index > 0 && (
                        <button {...backProps} style={{ 
                            background: 'transparent', border: 'none', color: '#6B7280', 
                            fontSize: '13px', fontWeight: '500', cursor: 'pointer', padding: '8px 12px' 
                        }}>
                            Back
                        </button>
                    )}
                    <button {...primaryProps} onClick={handlePrimary} style={{ 
                        background: '#8b5cf6', color: '#fff', border: 'none', 
                        borderRadius: '6px', fontSize: '13px', fontWeight: '600', 
                        cursor: 'pointer', padding: '8px 16px',
                        boxShadow: '0 2px 4px rgba(139, 92, 246, 0.2)'
                    }}>
                        {isLastStep ? 'Finish' : 'Next'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const WalkthroughTour = () => {
    const { user } = useAuth();
    const [run, setRun] = useState(false);

    useEffect(() => {
        if (!user) return;
        
        const forceTour = localStorage.getItem('oblique_force_tour') === 'true';
        const isDismissed = localStorage.getItem('oblique_walkthrough_dismissed') === 'true' || 
                            localStorage.getItem('oblique_tour_completed') === 'true' ||
                            (user?.uid && localStorage.getItem(`oblique_tour_completed_${user.uid}`) === 'true');

        if (!forceTour && isDismissed) {
            return;
        }

        if (forceTour) {
            setRun(true);
            return;
        }

        const isNewUser = user.metadata?.creationTime ? (Date.now() - new Date(user.metadata.creationTime).getTime()) < 24 * 60 * 60 * 1000 : false;
        
        if (isNewUser && !isDismissed) {
            const timer = setTimeout(() => {
                const stillDismissed = localStorage.getItem('oblique_walkthrough_dismissed') === 'true' || 
                                       localStorage.getItem('oblique_tour_completed') === 'true' ||
                                       (user?.uid && localStorage.getItem(`oblique_tour_completed_${user.uid}`) === 'true');
                if (!stillDismissed) {
                    setRun(true);
                }
            }, 1200);
            return () => clearTimeout(timer);
        }
    }, [user]);

    const steps = [
        {
            target: '#tour-center-anchor',
            content: (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <ObliqueIcon name="waving_hand" size={24} color="#8b5cf6" />
                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Welcome to Oblique!</h3>
                    </div>
                    <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
                        Let's take a quick 2-minute tour to help you get familiar with the core tools and navigation.
                    </p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
        },
        {
            target: '.desktop-sidebar',
            content: (
                <div>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>Find your way around</h3>
                    <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.5', margin: 0 }}>
                        Everything lives in this sidebar. Navigate seamlessly between your QMS dashboards, Campaigns, and Call Audits.
                    </p>
                </div>
            ),
            placement: 'right',
        },
        {
            target: '.tour-step-search',
            content: (
                <div>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>Global Command Center</h3>
                    <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.5', margin: 0 }}>
                        Hit ⌘K or Ctrl+K anytime to instantly search across all tools, deals, and standard operating procedures.
                    </p>
                </div>
            ),
            placement: 'bottom',
        },
        {
            target: '.tour-step-profile',
            content: (
                <div>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#111827' }}>Profile & Settings</h3>
                    <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.5', margin: 0 }}>
                        Manage your integrations (like Google Drive) here. You can also replay this tour anytime from your profile dropdown!
                    </p>
                </div>
            ),
            placement: 'bottom-end',
        }
    ];

    const handleJoyrideCallback = (data) => {
        const { status, action, type } = data;
        
        // Save completion on ANY terminal state, skip action, or if target is missing
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED, STATUS.ERROR];
        if (
            finishedStatuses.includes(status) || 
            action === 'skip' || 
            action === 'close' || 
            type === 'tour:end' || 
            type === 'tour:status' || 
            type === 'error:target_not_found' ||
            status === 'error'
        ) {
            try {
                localStorage.setItem('oblique_tour_completed', 'true');
                localStorage.setItem('oblique_walkthrough_dismissed', 'true');
                localStorage.removeItem('oblique_force_tour');
                if (user?.uid) {
                    localStorage.setItem(`oblique_tour_completed_${user.uid}`, 'true');
                }
            } catch (_) {}
            setRun(false);
        }
    };

    return (
        <>
            {run && <div id="tour-center-anchor" style={{ position: 'fixed', top: '50%', left: '50%', width: 0, height: 0, pointerEvents: 'none' }} />}
            <Joyride
                callback={handleJoyrideCallback}
                continuous
                hideCloseButton
                run={run}
                scrollToFirstStep
                steps={steps}
                tooltipComponent={CustomTooltip}
                styles={{
                    options: {
                        zIndex: 99999, // Ensure overlay covers everything
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    }
                }}
            />
        </>
    );
};

export default WalkthroughTour;
